import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TablePagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AssignmentIcon from '@mui/icons-material/Assignment';
import {
  useGetSuppliersQuery,
  useGetSupplierQuery,
  useToggleSupplierStatusMutation,
} from '../../../store/api/purchaseApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { SupplierFormContent } from './SupplierForm';

function SuppliersList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: suppliersData, isLoading, error } = useGetSuppliersQuery({
    all: 'true',
    page: page + 1,
    limit: rowsPerPage,
    search: debouncedSearch,
  });

  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  
  const suppliers = suppliersData?.data || (Array.isArray(suppliersData) ? suppliersData : (suppliersData?.suppliers || []));
  const totalCount = suppliersData?.total || suppliers.length;

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const [toggleStatus] = useToggleSupplierStatusMutation();
  const { data: editingSupplier, isLoading: loadingSupplier } = useGetSupplierQuery(editingId, {
    skip: !editingId,
  });



  const handleOpenAdd = () => {
    setEditingId(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (id) => {
    setEditingId(id);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleToggleStatus = (id) => {
    toggleStatus(id);
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading suppliers</Typography>;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 4,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/purchases')} size="small">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Suppliers
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd} sx={{ borderRadius: 2 }}>
          Add Supplier
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          label="Search suppliers"
          variant="outlined"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, code, phone or tax id..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 500 }}
        />
      </Box>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tax ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Outstanding Payables (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Advance / Credit (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suppliers.length > 0 ? (
                  suppliers.map((supplier) => (
                    <TableRow key={supplier.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{supplier.name}</TableCell>
                      <TableCell>{supplier.code || '-'}</TableCell>
                      <TableCell>{supplier.email || '-'}</TableCell>
                      <TableCell>{supplier.phone || '-'}</TableCell>
                      <TableCell>{supplier.taxId || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 180 }}>{supplier.address || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: Number(supplier.creditBalance) > 0 ? 'warning.main' : 'text.secondary' }}>
                        {formatAmount(supplier.creditBalance)}
                      </TableCell>
                      <TableCell>
                        {Number(supplier.advanceBalance) > 0 ? (
                          <Box sx={{ display: 'inline-block', bgcolor: 'success.light', color: 'success.dark', px: 1, py: 0.3, borderRadius: 1, fontSize: '0.8rem', fontWeight: 700 }}>
                            {formatAmount(supplier.advanceBalance)}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {formatAmount(0)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={!!supplier.isActive}
                          onChange={() => handleToggleStatus(supplier.id)}
                          color="primary"
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#4ECDC4',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#4ECDC4',
                            },
                          }}
                        />
                        <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                          {supplier.isActive ? 'Active' : 'Inactive'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/purchases/suppliers/${supplier.id}`)}
                          sx={{ mr: 0.5 }}
                          title="View supplier"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/purchases/orders/new?supplierId=${supplier.id}`)}
                          sx={{ mr: 0.5 }}
                          title="Create purchase order"
                        >
                          <ShoppingCartIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/purchases/orders/new?supplierId=${supplier.id}&lpo=1`)}
                          sx={{ mr: 0.5 }}
                          title="Create LPO (Local Purchase Order)"
                        >
                          <AssignmentIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="primary" onClick={() => handleOpenEdit(supplier.id)} title="Edit supplier">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No suppliers found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 1.5, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {editingId ? 'Edit Supplier' : 'Add Supplier'}
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 2,
            overflow: 'visible',
            '& .MuiInputLabel-root': { overflow: 'visible' },
          }}
        >
          <SupplierFormContent
            supplier={editingId ? editingSupplier : null}
            isEdit={!!editingId}
            loading={!!editingId && loadingSupplier}
            onSuccess={() => handleCloseDialog()}
            onCancel={handleCloseDialog}
            compact
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default SuppliersList;

